import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  Switch,
  Drawer,
  TextField,
  Paper,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { Eye, Clock, Trash2, X, Check, AlertCircle } from "react-feather";
import { useAuth } from "../../auth/workosAuth";
import DeleteConnectorModal from "../../components/Modals/DeleteConnector";
import Stack from "@mui/material/Stack";
import axios from "../../utils/axios";
import { config } from "../../constants";
import { DataGrid } from "@mui/x-data-grid";
import Table from "../../components/global/Table";
import "../../pages/jobs/DagHistory/daghistory.css";

const dataGridSx = {
  fontSize: "0.75rem",
  border: "none",
  "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.75rem", fontWeight: 600 },
  "& .MuiDataGrid-cell": { fontSize: "0.75rem", py: 1, display: "flex", alignItems: "center" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiTablePagination-toolbar": { alignItems: "center", minHeight: 40 },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.75rem", margin: 0 },
  "& .MuiTablePagination-select": { fontSize: "0.75rem" },
};

const Campaigns = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDatajoltUser = user?.email?.endsWith("@datajolt.io") || user?.email?.startsWith("maxime.broussard");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [toggling, setToggling] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteCampaign, setDeleteCampaign] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // History drawer state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCampaign, setHistoryCampaign] = useState(null);
  const [historyRuns, setHistoryRuns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);
  const [expandedRunTime, setExpandedRunTime] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [historySelectedIndex, setHistorySelectedIndex] = useState(0);
  const [historySearchQuery, setHistorySearchQuery] = useState("");

  const tooltipSlotProps = {
    arrow: { sx: { color: "#546e7a" } },
    tooltip: {
      sx: {
        backgroundColor: "#546e7a",
        fontSize: "11px",
        padding: "6px 12px",
        borderRadius: "6px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      },
    },
  };

  const customerTimezone = localStorage.getItem("timezone");

  const formatTimestamp = (value) => {
    if (!value) return "Never";
    const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true };
    if (customerTimezone && customerTimezone !== "undefined") opts.timeZone = customerTimezone;
    return new Date(value).toLocaleString("en-US", opts);
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${config.url.SMS_URL}/api/sms/campaigns`);
      setCampaigns(response.data.campaigns || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setError("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleToggleActive = async (campaign) => {
    setToggling(campaign.id);
    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/campaigns/${campaign.id}/toggle`);
      fetchCampaigns();
    } catch (err) {
      console.error("Error toggling campaign:", err);
      setError("Failed to toggle campaign");
    } finally {
      setToggling(null);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!deleteCampaign) return;
    setDeleting(true);
    try {
      await axios.delete(`${config.url.SMS_URL}/api/sms/campaigns/${deleteCampaign.id}`);
      setDeleteOpen(false);
      setDeleteCampaign(null);
      setSuccess("Campaign deleted successfully");
      setTimeout(() => setSuccess(null), 5000);
      fetchCampaigns();
    } catch (err) {
      console.error("Error deleting campaign:", err);
      setError(err.response?.data?.error || "Failed to delete campaign");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenHistory = async (campaign) => {
    setHistoryCampaign(campaign);
    setHistoryOpen(true);
    setHistoryRuns([]);
    setExpandedRun(null);
    setExpandedMessages([]);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${config.url.SMS_URL}/api/sms/campaigns/${campaign.id}/history`);
      setHistoryRuns(res.data.runs || []);
    } catch {
      setError("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleExpandRun = async (runId, runTime) => {
    if (expandedRun === runId) {
      setExpandedRun(null);
      setExpandedRunTime(null);
      setExpandedMessages([]);
      setHistorySelectedIndex(0);
      setHistorySearchQuery("");
      return;
    }
    setExpandedRun(runId);
    setExpandedRunTime(runTime);
    setExpandedLoading(true);
    setHistorySelectedIndex(0);
    setHistorySearchQuery("");
    try {
      const res = await axios.get(
        `${config.url.SMS_URL}/api/sms/campaigns/${historyCampaign.id}/history/detail?run_id=${encodeURIComponent(runId)}`
      );
      setExpandedMessages(res.data.messages || []);
      if (!runTime && res.data.messages?.length) setExpandedRunTime(res.data.messages[0].sent_at);
    } catch {} finally { setExpandedLoading(false); }
  };

  const campaignColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "campaign_type",
      headerName: "Campaign Type",
      width: 160,
      renderCell: (params) => {
        const labels = {
          one_off: "One-off",
          one_off_scheduled: "One-off Scheduled",
          dynamic: "Dynamic",
        };
        const label = labels[params.value] || "One-off";
        return (
          <Chip
            label={label}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.7rem", fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: "latest_commodities",
      headerName: "Commodities",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isOneOff = params.row.campaign_type === "one_off" || !params.row.campaign_type;
        if (isOneOff) return <Typography variant="body2" color="text.secondary">-</Typography>;
        const commodities = params.value;
        const count = Array.isArray(commodities) ? commodities.length : 0;
        return count > 0 ? (
          <Chip label={`${count} commodit${count === 1 ? "y" : "ies"}`} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        );
      },
    },
    {
      field: "latest_recipient_count",
      headerName: "Recipients",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const count = params.value;
        return count > 0 ? (
          <Chip label={`${count} recipient${count === 1 ? "" : "s"}`} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        );
      },
    },
    {
      field: "last_triggered",
      headerName: "Last Run",
      width: 150,
      renderCell: (params) => formatTimestamp(params.value),
    },
    {
      field: "next_trigger",
      headerName: "Next Run",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const campaign = params.row;
        if (campaign.campaign_type === "one_off") {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        if (campaign.campaign_type === "one_off_scheduled" && campaign.schedule_datetime) {
          const dt = new Date(campaign.schedule_datetime);
          return dt > new Date() ? formatTimestamp(campaign.schedule_datetime) : <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        if (campaign.campaign_type === "dynamic" && campaign.schedule_datetime) {
          const dt = new Date(campaign.schedule_datetime);
          if (campaign.schedule_recurrence) {
            const timeOpts = { hour: "numeric", minute: "2-digit", hour12: true };
            if (customerTimezone && customerTimezone !== "undefined") timeOpts.timeZone = customerTimezone;
            return `${campaign.schedule_recurrence.charAt(0).toUpperCase() + campaign.schedule_recurrence.slice(1)} @ ${dt.toLocaleTimeString("en-US", timeOpts)}`;
          }
          return dt > new Date() ? formatTimestamp(campaign.schedule_datetime) : "Past";
        }
        return "-";
      },
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Switch
          checked={params.value !== false}
          onChange={(e) => { e.stopPropagation(); handleToggleActive(params.row); }}
          size="small"
          disabled={!isDatajoltUser || toggling === params.row.id}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const campaign = params.row;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "nowrap" }}>
            <Tooltip title="History" placement="top" arrow slotProps={tooltipSlotProps}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenHistory(campaign); }}>
                <Clock size={16} />
              </IconButton>
            </Tooltip>
{isDatajoltUser && (
              <Tooltip title="Delete" placement="top" arrow slotProps={tooltipSlotProps}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteCampaign(campaign); setDeleteOpen(true); }} sx={{ color: "error.main" }}>
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const renderHistoryContent = () => {
    if (historyLoading) {
      return (
        <Box sx={{ p: 1, overflowY: "auto", flex: 1 }}>
          <Table
            isLoading={false}
            sortedColumn={{ column: "", isAsc: true }}
            onSortColumn={() => {}}
            headings={[
              { Children: () => "Date / Time", columnName: "run_time", isSortable: false },
              { Children: () => "Sent", columnName: "total_sms", isSortable: false },
              { Children: () => "Failed", columnName: "failed_count", isSortable: false },
              { Children: () => "Channel", columnName: "channel", isSortable: false },
            ]}
            rows={[1, 2, 3, 4].map((i) => ({
              key: `skeleton-${i}`,
              columns: [
                { type: "component", component: <Skeleton variant="rounded" height={18} sx={{ borderRadius: 1 }} /> },
                { type: "component", component: <Skeleton variant="rounded" height={18} sx={{ borderRadius: 1 }} /> },
                { type: "component", component: <Skeleton variant="rounded" height={18} sx={{ borderRadius: 1 }} /> },
                { type: "component", component: <Skeleton variant="rounded" height={18} sx={{ borderRadius: 1 }} /> },
              ],
            }))}
          />
        </Box>
      );
    }

    if (historyRuns.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ fontSize: "0.8rem" }}>No sends recorded for this campaign.</Alert>
        </Box>
      );
    }

    if (!expandedRun) {
      return (
        <Box sx={{ p: 1, overflowY: "auto", flex: 1 }}>
          <Table
            isLoading={false}
            sortedColumn={{ column: "", isAsc: true }}
            onSortColumn={() => {}}
            headings={[
              { Children: () => "Date / Time", columnName: "run_time", isSortable: false },
              { Children: () => "Sent", columnName: "total_sms", isSortable: false },
              { Children: () => "Failed", columnName: "failed_count", isSortable: false },
              { Children: () => "Channel", columnName: "channel", isSortable: false },
            ]}
            rows={historyRuns.map((run, idx) => ({
              key: `run-${idx}`,
              onClick: () => handleExpandRun(run.campaign_run_id, run.run_time),
              columns: [
                { type: "text", text: formatTimestamp(run.run_time) },
                { type: "text", text: String(run.total_sms) },
                { type: "text", text: String(run.failed_count || 0) },
                {
                  type: "component",
                  component: <Chip label={run.channel} size="small" color="primary" variant="outlined" />,
                },
              ],
            }))}
          />
        </Box>
      );
    }

    // Expanded run detail
    const filteredHistory = expandedMessages.filter((msg) => {
      if (!historySearchQuery) return true;
      const q = historySearchQuery.toLowerCase();
      return (msg.person_name || "").toLowerCase().includes(q) ||
        (msg.company_name || "").toLowerCase().includes(q) ||
        (msg.phone || "").toLowerCase().includes(q);
    });
    const activeHistoryMsg = expandedMessages[historySelectedIndex] || expandedMessages[0];

    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {/* Back + run info */}
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
          <Button size="small" onClick={() => { setExpandedRun(null); setExpandedMessages([]); }} sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: 0, px: 1 }}>
            Back
          </Button>
          {expandedRunTime && (
            <Typography variant="caption" color="text.secondary">
              {formatTimestamp(expandedRunTime)} - {expandedMessages.length} messages
            </Typography>
          )}
        </Box>

        {expandedLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
        ) : expandedMessages.length === 0 ? (
          <Box sx={{ p: 2 }}><Alert severity="info">No messages found for this run.</Alert></Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", flex: 1, overflow: "hidden", height: "calc(100vh - 120px)" }}>
            {/* Recipient list (left panel) */}
            <Box sx={{ width: 260, minWidth: 260, borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column" }}>
              <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                <TextField
                  size="small"
                  placeholder="Search recipients..."
                  value={historySearchQuery}
                  onChange={(e) => setHistorySearchQuery(e.target.value)}
                  fullWidth
                  InputProps={{ sx: { height: 30, fontSize: "0.75rem" } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {filteredHistory.length} of {expandedMessages.length} recipients
                </Typography>
              </Box>
              <Box sx={{ overflowY: "auto", flex: 1 }}>
                {filteredHistory.map((msg) => {
                  const originalIdx = expandedMessages.indexOf(msg);
                  const isSelected = originalIdx === historySelectedIndex;
                  return (
                    <Box
                      key={originalIdx}
                      onClick={() => setHistorySelectedIndex(originalIdx)}
                      sx={{
                        px: 1.5, py: 0.75, cursor: "pointer",
                        bgcolor: isSelected ? "primary.main" : "transparent",
                        color: isSelected ? "white" : "text.primary",
                        "&:hover": { bgcolor: isSelected ? "primary.main" : "action.hover" },
                        borderBottom: 1, borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400, fontSize: "0.75rem" }}>
                          {msg.person_name || msg.company_name || "Unnamed"}
                        </Typography>
                        {(msg.send_status || "sent") === "sent"
                          ? <Check size={12} color={isSelected ? "#fff" : "#2e7d32"} style={{ flexShrink: 0 }} />
                          : <AlertCircle size={12} color={isSelected ? "#fff" : "#d32f2f"} style={{ flexShrink: 0 }} />
                        }
                      </Box>
                      {msg.company_name && (
                        <Typography variant="caption" sx={{ fontSize: "0.65rem", color: isSelected ? "rgba(255,255,255,0.7)" : "text.secondary" }}>
                          {msg.company_name}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
            {/* Message preview (right panel) */}
            {activeHistoryMsg && (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* iPhone-style header */}
                <Box sx={{ bgcolor: "#f5f5f5", px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                  >
                    {activeHistoryMsg?.person_name || activeHistoryMsg?.company_name || "Customer"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                    {activeHistoryMsg?.phone || ""}
                  </Typography>
                </Box>
                {/* Message bubble area */}
                <Box sx={{ flex: 1, bgcolor: "#fff", p: 2, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <Box sx={{
                      bgcolor: "#34C759", color: "white",
                      borderRadius: "12px 12px 4px 12px", px: 2, py: 1.5, maxWidth: "80%",
                    }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: "0.8rem", lineHeight: 1.5 }}>
                        {activeHistoryMsg?.message || "(No message)"}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right", fontSize: "0.65rem" }}>
                    {activeHistoryMsg?.sent_at ? formatTimestamp(activeHistoryMsg.sent_at) : ""} · {activeHistoryMsg?.channel}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Helmet title="Datajolt | Campaigns" />
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", margin: "12px 8px", height: "45px" }}>
          <h4 style={{ marginBottom: 0 }}>
            <b style={{ color: "#090E47" }}>Outreach Campaigns ({campaigns.length})</b>
          </h4>
          <Box sx={{ display: "flex", marginLeft: "auto", alignItems: "center", gap: 1 }}>
            {!loading && (
              <Button
                variant="contained"
                onClick={() => navigate("/crm/new")}
                disabled={!isDatajoltUser}
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  height: "32px",
                  padding: "0 16px",
                  textTransform: "none",
                  borderRadius: "6px",
                  background: "#2D49FD",
                  border: "1px solid #2D49FD",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                One Off Blast <AddIcon sx={{ marginLeft: "5px", fontSize: 14 }} />
              </Button>
            )}
          </Box>
        </Box>
        <hr style={{ margin: "0 8px" }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2, mx: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, mx: 1 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Campaigns Table */}
        <Box sx={{ flex: 1, height: "calc(100vh - 150px)", px: 1 }}>
          <DataGrid
            rows={campaigns}
            columns={campaignColumns}
            density="compact"
            loading={loading}
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            onCellClick={(params) => {
              if (params.field === "is_active" || params.field === "actions") return;
              if (params.row.campaign_type !== "dynamic") {
                toast.info("Campaign details are only available for dynamic campaigns");
                return;
              }
              navigate(`/crm/campaigns/${params.row.id}`);
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={dataGridSx}
          />
        </Box>

      </Box>

      {/* History Drawer */}
      <Drawer
        anchor="right"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        PaperProps={{ sx: { width: "65vw", display: "flex", flexDirection: "column" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            History{historyCampaign ? ` - ${historyCampaign.name}` : ""}
          </Typography>
          <IconButton size="small" onClick={() => setHistoryOpen(false)}><X size={16} /></IconButton>
        </Box>
        {renderHistoryContent()}
      </Drawer>

      <DeleteConnectorModal
        isShown={deleteOpen}
        onHide={() => { setDeleteOpen(false); setDeleteCampaign(null); }}
        onAction={handleDeleteCampaign}
        deleteData={{ deleting }}
        text={{
          bold: deleteCampaign?.name || "",
          regular: "campaign",
          regularFirst: true,
        }}
      />
    </>
  );
};

export default Campaigns;
